import { useEffect, useRef } from 'react';

import { type TProductDataRegions, type TProductEdition } from '@atlassian/search-client/fetch-search-config';
import { type BackendExperiment, type ExperimentConfig } from '@atlassian/search-experiment/types';

import { QueryState } from '../../../common/constants/analytics';
import type { ProductKeys } from '../../../common/constants/products';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { searchResultsLoadUFOExperienceSuccess } from '../../../common/ui/ufo/experiences';
import { onAdvancedSearchResultsShown } from '../../../common/utils/events/advanced-search-results-shown';
import { onFullPageSearchOperational } from '../../../common/utils/events/full-page-search-operational';
import type {
	CommonAttributes,
	NonPrivacySafeAttributes,
	ResultShown,
} from '../../../common/utils/events/types';
import { serveSearchPageTimeoutsUX } from '../../../common/utils/experiments';
import { getIsBulkConnectBannerShown } from '../../../common/utils/quick-find/analytics';
import { getExtensionData } from '../../../common/utils/relay/get-extension-data';
import { useAnalytics } from '../../../controllers/analytics';
import { useSearchAndResultAnalyticsAttributes } from '../../../controllers/analytics/search-and-response-analytics';
import { useSocialSignalsAnalyticsAttributes } from '../../../controllers/analytics/social-signal-analytics';
import type { SelectedFiltersReturnType } from '../../../controllers/filters/selected/get-selected-filters';

type useSearchResultsAnalyticsArgs<TEdge> = {
	are3pResultsShown: boolean;
	backendExperimentRef: React.RefObject<BackendExperiment>;
	clearReferrer: () => void | Promise<void>;
	cloudId: string | undefined;
	commonAttributes: CommonAttributes;
	edges: TEdge[];
	experimentConfig: ExperimentConfig;
	getSelectedFilters: () => SelectedFiltersReturnType;
	intentDetectionTriggered?: boolean;
	isLoadingNext: boolean;
	isJiraIssueIndexed: boolean;
	isPaginatedQuery: boolean;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	productDataRegions: TProductDataRegions | undefined;
	productEdition: TProductEdition | undefined;
	productsWithTimeoutsFromErrors: ProductKeys[] | undefined;
	referrer: string | undefined;
	resultsForAnalytics: IAnalyticsSearchPageResults;
	resultsShown: ResultShown[];
	searchQuery: string;
};

export const useSearchResultsAnalytics: <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
}: useSearchResultsAnalyticsArgs<TEdge>) => void = <TEdge>({
	are3pResultsShown,
	backendExperimentRef,
	clearReferrer,
	cloudId,
	commonAttributes,
	edges,
	experimentConfig,
	getSelectedFilters,
	intentDetectionTriggered,
	isLoadingNext,
	isJiraIssueIndexed,
	isPaginatedQuery,
	nonPrivacySafeAttributes,
	productDataRegions,
	productEdition,
	productsWithTimeoutsFromErrors,
	referrer,
	resultsForAnalytics,
	resultsShown,
	searchQuery,
}: useSearchResultsAnalyticsArgs<TEdge>) => {
	const { fireAnalyticsEvent } = useAnalytics();

	// Properties related to social signals
	const socialSignalsAttributes = useSocialSignalsAnalyticsAttributes(resultsForAnalytics);

	// Properties related to the search results in the result set
	const { searchAndResult } = useSearchAndResultAnalyticsAttributes(resultsForAnalytics);

	// We store these values in a ref as the references to the properties are not
	// stable we do not want to trigger the analytics events unnecessarily. This
	// allows to narrow down the dependency array of the useEffect hook that fires
	// the analytics events.
	const analyticsProperties = {
		are3pResultsShown,
		clearReferrer,
		cloudId,
		commonAttributes,
		experimentConfig,
		fireAnalyticsEvent,
		getSelectedFilters,
		intentDetectionTriggered,
		isJiraIssueIndexed,
		isPaginatedQuery,
		nonPrivacySafeAttributes,
		productDataRegions,
		productEdition,
		productsWithTimeoutsFromErrors,
		referrer,
		resultsShown,
		searchAndResult,
		searchQuery,
		socialSignalsAttributes,
	};
	const analyticsPropertiesRef = useRef(analyticsProperties);
	analyticsPropertiesRef.current = analyticsProperties;

	useEffect(() => {
		const {
			are3pResultsShown,
			clearReferrer,
			commonAttributes: commonAttributesWithoutExtensionData,
			fireAnalyticsEvent,
			intentDetectionTriggered,
			isPaginatedQuery,
			nonPrivacySafeAttributes,
			productsWithTimeoutsFromErrors,
			referrer,
			resultsShown,
			searchAndResult,
			searchQuery,
			socialSignalsAttributes,
		} = analyticsPropertiesRef.current;

		if (isLoadingNext) {
			return;
		}

		const numResults = edges.length;
		const isBulkConnectBannerShown = getIsBulkConnectBannerShown();

		const commonAttributes = {
			...commonAttributesWithoutExtensionData,
			...getExtensionData(commonAttributesWithoutExtensionData.isCQL),
		};

		const requestedBackendExperiment: BackendExperiment = backendExperimentRef.current ?? undefined;

		fireAnalyticsEvent(
			onAdvancedSearchResultsShown({
				queryState: searchQuery ? QueryState.POSTQUERY : QueryState.PREQUERY,
				numResults,
				shownCount: numResults,
				are3pResultsShown,
				commonAttributes,
				nonPrivacySafeAttributes,
				socialSignalsAttributes,
				resultsShown,
				searchAndResult,
				requestedBackendExperiments: {
					backendExperiments: requestedBackendExperiment,
				},
				referrer: referrer && searchQuery ? referrer : undefined,
				isPaginatedQuery,
				isBulkConnectBannerShown,
				productsWithTimeout: serveSearchPageTimeoutsUX()
					? productsWithTimeoutsFromErrors || []
					: undefined,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
			}),
		);

		fireAnalyticsEvent(
			onFullPageSearchOperational({
				success: true,
				commonAttributes,
			}),
		);

		searchResultsLoadUFOExperienceSuccess({
			queryLength: commonAttributes.queryLength,
			wordCount: commonAttributes.wordCount,
		});

		// Ensure that the referrer attribute is only used once
		clearReferrer();
	}, [analyticsPropertiesRef, backendExperimentRef, edges, isLoadingNext]);
};
